/** Small mono-cased status pill. Same visual convention across all
 * status enums (job status, walkthrough status, quote status) so
 * the admin learns one mental model. */

interface StatusBadgeProps {
  status: string;
}

const STATUS_COLORS: Record<string, string> = {
  // Job statuses
  open: "bg-pencil-yellow-soft text-graphite",
  quoted: "bg-graphite text-paper",
  won: "bg-olive/15 text-olive",
  in_progress: "bg-pencil-yellow text-graphite",
  complete: "bg-olive/25 text-olive",
  lost: "bg-red-pencil/15 text-red-pencil",
  archived: "bg-rule/30 text-graphite-soft",

  // Walkthrough statuses
  collecting: "bg-paper text-graphite-soft",
  uploading: "bg-burnt/15 text-burnt",
  uploaded: "bg-pencil-yellow-soft text-graphite",
  processing: "bg-burnt/15 text-burnt",
  processed: "bg-olive/15 text-olive",
  failed: "bg-red-pencil/15 text-red-pencil",

  // Quote statuses (live tinting per lifecycle state — accepted is
  // celebratory, expired is the "needs attention" warning, declined
  // is terminal-negative). Lets the contractor scan a list of
  // quotes and immediately spot what to act on.
  draft: "bg-rule/30 text-graphite-soft",
  ready: "bg-pencil-yellow-soft text-graphite",
  sent: "bg-graphite text-paper",
  accepted: "bg-olive/20 text-olive",
  declined: "bg-red-pencil/15 text-red-pencil",
  expired: "bg-burnt/20 text-burnt",
};

export function StatusBadge({ status }: StatusBadgeProps) {
  const klass = STATUS_COLORS[status] ?? "bg-graphite-faint/15 text-graphite-soft";
  return (
    <span
      className={`inline-flex items-center rounded-sm px-2 py-0.5 font-mono text-[0.6875rem] font-medium uppercase tracking-wider ${klass}`}
    >
      {status}
    </span>
  );
}
